import React, { createContext, useContext, useMemo, useRef } from "react";

// Context value: a mutable registry of pagePath -> Set(canvasKey)
interface BrazeCanvasRegistry {
	shouldClaimCanvas: (pagePath: string, canvasKey: string) => boolean;
}

const BrazeCanvasContext = createContext<BrazeCanvasRegistry | null>(null);

export const BrazeCanvasProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	// registryRef maps a page path to a set of claimed canvas keys
	const registryRef = useRef<Map<string, Set<string>>>(new Map());

	const value = useMemo<BrazeCanvasRegistry>(
		() => ({
			shouldClaimCanvas: (pagePath: string, canvasKey: string) => {
				if (!pagePath || !canvasKey) return true;
				const existing = registryRef.current.get(pagePath) || new Set<string>();
				if (existing.has(canvasKey)) {
					return false; // already claimed by an earlier module on this page
				}
				existing.add(canvasKey);
				registryRef.current.set(pagePath, existing);
				return true;
			},
		}),
		[],
	);

	return (
		<BrazeCanvasContext.Provider value={value}>
			{children}
		</BrazeCanvasContext.Provider>
	);
};

export const useBrazeCanvasRegistry = (): BrazeCanvasRegistry => {
	const ctx = useContext(BrazeCanvasContext);
	if (!ctx) {
		// If not wrapped, default to allowing claims to avoid breaking pages
		return { shouldClaimCanvas: () => true };
	}
	return ctx;
};
